import React from "react";
import { IUserAccount } from "../types/common";
import { BasicInformation } from "./components/BasicInformation";
import { H1 } from "../../components/utils/Headings";
import EditableTitle from "../Common/Components/EditableTitle";
import AccountPassword from "./components/AccountPassword";
import { IEditAccountOperations, ElementState, IModalSettings } from "./types";
import { LocaleNames, PreferredLocale } from "../../utils/locales";
import PreferredLanguageSelector from "./components/PreferredLanguageSelector";
import AccountFeatureAccess from "./components/AccountFeatureAccess";
import { Section } from "../Common/Styles/containers";
import { ClientFeature } from "../../parsers/login";
import { OwningOrganization } from "./components/OwningOrganization";
import { AnyAction, Dispatch } from "redux";
import { push } from "connected-react-router";
import { connect } from "react-redux";
import { IOrganization, IState } from "../reducer";
import { Creators as AllCreators } from "../../actions";
import ActionConfirmationButton from "../../components/pages/CourseAuthor/ActionConfirmation";
import { FaTrash } from "react-icons/fa";
import { DeleteButton } from "../Common/Components/FormComponents";
import styled from "styled-components";
import CustomModal, { ModalType } from "../../components/utils/CustomModal";

const Creators = (AllCreators as any).App.SelectOrganization;

const CustomDeleteAccBtn = styled(DeleteButton)`
  display: flex;
  gap: 0.5rem;
  flex-align: center;
  padding-left: 1.5rem;
`;

const ActionBtnContainer = styled.div`
  width: 15rem;
`;

const ModalContainer = styled.div`
  width: 45rem;
  position: fixed;
  right: 1.5em;
  bottom: 1.5em;
  z-index: 1;
`;

export interface IAccountEditorOwnProps {
  activeUserId: string;
  activeOrgId: string;
  accountAlias: ElementState;
  accountId: string;
  clientFeatures: ClientFeature[] | undefined;
  emailAddress: ElementState;
  isOrgAdmin: boolean;
  selectedAccount: IUserAccount | undefined;
  selectedLocale: PreferredLocale | null;
  setAccountAlias: React.Dispatch<React.SetStateAction<ElementState>>;
  setClientFeatures: React.Dispatch<React.SetStateAction<ClientFeature[] | undefined>>;
  setEmailAddress: React.Dispatch<React.SetStateAction<ElementState>>;
  setSelectedLocale: React.Dispatch<React.SetStateAction<PreferredLocale | null>>;
  editAccountOperations: IEditAccountOperations;
}

interface IAccountEditorStateProps {
  organizations: IOrganization[];
}

interface IAccountEditorDispatchProps {
  fetchOrgs: () => any;
  onDeleteAccountSuccess: () => void;
  reloadUsersAmount: () => void;
}

type AccountEditorProps = IAccountEditorOwnProps & IAccountEditorStateProps & IAccountEditorDispatchProps;

const AccountEditor = (props: AccountEditorProps) => {
  const {
    accountId,
    accountAlias,
    activeOrgId,
    fetchOrgs,
    setAccountAlias,
    emailAddress,
    setEmailAddress,
    selectedAccount,
    selectedLocale,
    setSelectedLocale,
    editAccountOperations,
    activeUserId,
    clientFeatures,
    setClientFeatures,
    isOrgAdmin,
    onDeleteAccountSuccess,
    organizations,
    reloadUsersAmount,
  } = props;

  const [currentTitle, setCurrentTitle] = React.useState<string | undefined>(undefined);
  const supportedLocales = Object.keys(LocaleNames) as PreferredLocale[];
  const [selectedOrganization, setSelectedOrganization] = React.useState<string>(activeOrgId);
  const [modalSettings, setModalSettings] = React.useState<IModalSettings>({ modalMsg: undefined, showModal: false });

  React.useEffect(() => fetchOrgs, []);

  React.useEffect(() => {
    setCurrentTitle(selectedAccount?.displayName);
  }, [selectedAccount?.displayName]);

  React.useEffect(() => {
    if (selectedAccount?.preferredLocale) {
      setSelectedLocale(selectedAccount?.preferredLocale);
    }
  }, [selectedAccount?.preferredLocale]);

  React.useEffect(() => {
    if (selectedAccount?.owner) {
      setSelectedOrganization(selectedAccount.owner);
    }
  }, [selectedAccount?.owner]);

  const handleModifyTitle = (title: string) => {
    editAccountOperations.modifyTitle(title).then(() => setCurrentTitle(title));
  };

  const handleSelectLocale = (newLocale: PreferredLocale) => {
    editAccountOperations.modifyPreferredLocale(newLocale).then(() => setSelectedLocale(newLocale));
  };

  const handleDeleteAccount = () => {
    editAccountOperations
      .deleteAccount()
      .then(onDeleteAccountSuccess)
      .then(reloadUsersAmount);
  };

  const setModalMessage = (modalMsg: string) => {
    setModalSettings({ modalMsg, showModal: true });
    setTimeout(closeModal, 2000);
  };
  const closeModal = () => setModalSettings({ ...modalSettings, showModal: false });

  return (
    <>
      <H1>
        <EditableTitle
          addTitle={handleModifyTitle}
          modifyTitle={handleModifyTitle}
          currentTitle={currentTitle || "New Account"}
          defaultTitle={"Account Name"}
          removeTitle={true}
          isMainTitle={true}
        />
      </H1>
      <Section>
        <BasicInformation
          accountAlias={accountAlias}
          setAccountAlias={setAccountAlias}
          addAccountAlias={editAccountOperations.addAccountAlias}
          modifyAccountAlias={editAccountOperations.modifyAccountAlias}
          deleteAccountAlias={editAccountOperations.deleteAccountAlias}
          undeleteAccountAlias={editAccountOperations.undeleteAccountAlias}
          activeUserId={activeUserId}
          emailAddress={emailAddress}
          setEmailAddress={setEmailAddress}
          addEmailAddress={editAccountOperations.addEmailAddress}
          modifyEmailAddress={editAccountOperations.modifyEmailAddress}
          deleteEmailAddress={editAccountOperations.deleteEmailAddress}
          undeleteEmailAddress={editAccountOperations.undeleteEmailAddress}
          selectedAccount={selectedAccount}
          activeOrgId={activeOrgId}
          accountId={accountId}
        />
        <AccountPassword modifyPassword={editAccountOperations.modifyPassword} />
        <br />
        <PreferredLanguageSelector
          supportedLocales={supportedLocales}
          selectedLocale={selectedLocale}
          onSelectLocale={handleSelectLocale}
          setModalMessage={setModalMessage}
        />
        <br />
        <AccountFeatureAccess
          clientFeatures={clientFeatures}
          setClientFeatures={setClientFeatures}
          insertClientFeature={editAccountOperations.insertClientFeature}
          deleteClientFeature={editAccountOperations.deleteClientFeature}
          setModalMessage={setModalMessage}
        />
        {isOrgAdmin && (
          <OwningOrganization
            accountAlias={accountAlias}
            selectedOrg={selectedOrganization}
            setSelectedOrg={setSelectedOrganization}
            changeOwningOrg={editAccountOperations.modifyOwningOrg}
            migrateAccountAlias={editAccountOperations.migrateAccountAlias}
            setModalMessage={setModalMessage}
            organizations={organizations}
          />
        )}
        <ActionBtnContainer data-testid="delete-acc-btn">
          <ActionConfirmationButton
            action="Delete"
            actionFn={handleDeleteAccount}
            ActionIcon={FaTrash}
            CustomActionButton={CustomDeleteAccBtn}
            customBtnText="Delete Account"
          />
        </ActionBtnContainer>
        <CustomModal
          customContainer={ModalContainer}
          customCardProps={{ maxWidth: "100%" }}
          className="global-indicator"
          title="Saved changes"
          showModal={modalSettings.showModal}
          type={ModalType.Success}
          closeModal={closeModal}
          message={modalSettings.modalMsg}
        />
      </Section>
    </>
  );
};

const mapStateToProps = (state: IState): IAccountEditorStateProps => ({
  organizations: state.App.SelectOrganization._.organizations || [],
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IAccountEditorDispatchProps => ({
  fetchOrgs: () => dispatch(Creators.fetchAllOrganizations()),
  onDeleteAccountSuccess: () => dispatch(push(`/accounts`)),
  reloadUsersAmount: () => dispatch(Creators.loadUsersAmount()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AccountEditor);
